/**
 * URLInfoModal.tsx - Central URL/API documentation modal for plugins
 * 
 * CHANGE HISTORY:
 * - 251122_1307 danielbeer: Created central URL info modal
 * 
 * Shows live URL for current plugin with all available parameters,
 * data submission/return formats, and FileMaker integration instructions.
 */

import React, { useState, useMemo } from 'react';
import { pluginRegistry } from '../pluginRegistry';
import type { ToolType } from '../types';

interface URLInfoModalProps {
  currentTool: ToolType;
  onClose: () => void;
}

export const URLInfoModal: React.FC<URLInfoModalProps> = ({ currentTool, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  // Get current plugin info
  const plugin = useMemo(() => {
    return pluginRegistry.find(p => p.id === currentTool);
  }, [currentTool]);

  // Build base URL
  const baseURL = useMemo(() => {
    return window.location.origin + window.location.pathname;
  }, []);

  // URL parameters for this plugin
  const urlParams = useMemo(() => {
    const params: Record<string, { description: string; example: string; required?: boolean }> = {
      tool: {
        description: 'Plugin to load',
        example: currentTool,
        required: true
      }
    };

    // Add plugin-specific parameters
    if (currentTool === 'pdf-viewer') {
      params.url = { description: 'PDF file URL to load', example: 'https://example.com/file.pdf' };
      params.page = { description: 'Initial page number', example: '1' };
    } else if (currentTool === 'office-viewer') {
      params.url = { description: 'Office file URL to load', example: 'https://example.com/file.docx' };
      params.type = { description: 'File type (docx, xlsx, pptx)', example: 'docx' };
    } else if (currentTool === 'field-matcher') {
      params.sourceData = { description: 'JSON data to match', example: encodeURIComponent(JSON.stringify([{name: 'John'}])) };
      params.targetSchema = { description: 'Target field schema', example: encodeURIComponent(JSON.stringify({firstName: 'text'})) };
    } else if (currentTool === 'outliner') {
      params.fieldView = { description: 'Field view mode', example: 'single or multi' };
      params.data = { description: 'Initial outliner data (JSON)', example: encodeURIComponent(JSON.stringify({nodes: []})) };
    }

    // Common parameters
    params.showToolbar = { description: 'Show navigation toolbar', example: 'true or false (default: false when tool param present)' };
    
    return params;
  }, [currentTool]);

  // Build example URL
  const exampleURL = useMemo(() => {
    const params = new URLSearchParams();
    params.set('tool', currentTool);
    
    // Add one example param if available
    const firstParam = Object.keys(urlParams).find(k => k !== 'tool' && k !== 'showToolbar');
    if (firstParam && urlParams[firstParam]) {
      params.set(firstParam, urlParams[firstParam].example);
    }
    
    return `${baseURL}?${params.toString()}`;
  }, [baseURL, currentTool, urlParams]);

  // Copy URL to clipboard
  const copyURL = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Data submission/return info
  const dataInfo = useMemo(() => {
    const info: { submit?: string; return?: string; example?: string } = {};

    if (currentTool === 'field-matcher') {
      info.submit = 'Pass sourceData and targetSchema as URL-encoded JSON in URL parameters';
      info.return = 'Matched data is available via FileMaker.PerformScript callback';
      info.example = `
// FileMaker Script to call Field Matcher
Set Variable [ $sourceData ; JSONSetElement ( "[]" ; 
  "[0].name" ; "John Doe" ; JSONString 
) ]
Set Variable [ $targetSchema ; JSONSetElement ( "{}" ;
  "firstName" ; "text" ; JSONString ;
  "lastName" ; "text" ; JSONString
) ]
Set Variable [ $url ; "${baseURL}?tool=field-matcher&sourceData=" & 
  GetAsURLEncoded ( $sourceData ) & 
  "&targetSchema=" & GetAsURLEncoded ( $targetSchema ) 
]
Set Web Viewer [ Object Name: "FMwvTool" ; URL: $url ]
`;
    } else if (currentTool === 'outliner') {
      info.submit = `📤 Export to FileMaker: Click toolbar button or use FileMaker.PerformScript("FMwvTool_ImportOutlinerData", jsonData)`;
      info.return = `📥 Import from FileMaker: Click toolbar button or use FileMaker.PerformScript("FMwvTool_ExportOutlinerData", "")`;
      info.example = `
# TOOLBAR BUTTONS (Easiest Method)
📤 Export to FileMaker - Sends current outliner data to FileMaker
📥 Import from FileMaker - Loads data from FileMaker into outliner

# FILEMAKER SCRIPT: FMwvTool_ImportOutlinerData
# (Receives data from Export button)
Set Variable [ $json ; Get ( ScriptParameter ) ]
Set Variable [ $nodes ; JSONGetElement ( $json ; "nodes" ) ]
Set Variable [ $count ; JSONGetElementCount ( $nodes ) ]
Set Variable [ $i ; 0 ]
Loop
  Exit Loop If [ $i ≥ $count ]
  Set Variable [ $node ; JSONGetElement ( $nodes ; "[" & $i & "]" ) ]
  Set Variable [ $text ; JSONGetElement ( $node ; "text" ) ]
  Set Variable [ $parentId ; JSONGetElement ( $node ; "parentId" ) ]
  New Record/Request
  Set Field [ Tasks::Name ; $text ]
  Set Field [ Tasks::ParentID ; $parentId ]
  Commit Records/Requests
  Set Variable [ $i ; $i + 1 ]
End Loop
Show Custom Dialog [ "Import Complete" ; $count & " nodes imported" ]

# FILEMAKER SCRIPT: FMwvTool_ExportOutlinerData
# (Returns data for Import button)
Set Variable [ $json ; "{\\"nodes\\":[" ]
Go to Layout [ "Tasks" ]
Go to Record/Request/Page [ First ]
Loop
  Exit Loop If [ Get ( RecordNumber ) > Get ( FoundCount ) ]
  Set Variable [ $node ; "{" & 
    "\\"id\\":\\"" & Tasks::ID & "\\"," &
    "\\"text\\":\\"" & Tasks::Name & "\\"," &
    "\\"parentId\\":" & If ( IsEmpty ( Tasks::ParentID ) ; "null" ; "\\"" & Tasks::ParentID & "\\"" ) & "," &
    "\\"generation\\":" & Tasks::Level & "," &
    "\\"familyId\\":\\"family-1\\"," &
    "\\"children\\":[]" &
    "}"
  ]
  Set Variable [ $json ; $json & $node ]
  Go to Record/Request/Page [ Next ; Exit after last: On ]
  Set Variable [ $json ; $json & If ( Get ( RecordNumber ) ≤ Get ( FoundCount ) ; "," ; "" ) ]
End Loop
Set Variable [ $json ; $json & "]}" ]
Exit Script [ Text Result: $json ]
`;
    } else if (currentTool === 'pdf-viewer') {
      info.submit = 'Pass PDF URL in url parameter';
      info.return = 'Current page number available via JavaScript: window.pdfViewer.currentPage';
      info.example = `
// Load PDF
Set Variable [ $pdfURL ; "https://example.com/document.pdf" ]
Set Variable [ $url ; "${baseURL}?tool=pdf-viewer&url=" & GetAsURLEncoded ( $pdfURL ) ]
Set Web Viewer [ Object Name: "FMwvTool" ; URL: $url ]

// Get current page
Perform JavaScript in Web Viewer [ "FMwvTool" ;
  "window.pdfViewer ? window.pdfViewer.currentPage : 1"
]
Set Variable [ $currentPage ; Get ( ScriptResult ) ]
`;
    }

    return info;
  }, [currentTool, baseURL]);

  return (
    <div className="url-info-modal-overlay" onClick={onClose}>
      <div className="url-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="url-info-header">
          <h2>🔗 URL & API Info: {plugin?.name || currentTool}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="url-info-content">
          {/* Current URL */}
          <section className="url-section">
            <h3>Current URL</h3>
            <div className="url-display">
              <code>{exampleURL}</code>
              <button onClick={() => copyURL(exampleURL)} className="copy-button">
                {copied ? '✓ Copied!' : '📋 Copy'}
              </button>
            </div>
          </section>

          {/* URL Parameters */}
          <section className="params-section">
            <h3>Available URL Parameters</h3>
            <table className="params-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Description</th>
                  <th>Example</th>
                  <th>Required</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(urlParams).map(([param, info]) => (
                  <tr key={param}>
                    <td><code>{param}</code></td>
                    <td>{info.description}</td>
                    <td><code>{info.example}</code></td>
                    <td>{info.required ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Data Submission/Return */}
          {(dataInfo.submit || dataInfo.return) && (
            <section className="data-section">
              <h3>Data Integration</h3>
              
              {dataInfo.submit && (
                <div className="data-item">
                  <h4>📤 Submitting Data</h4>
                  <p>{dataInfo.submit}</p>
                </div>
              )}
              
              {dataInfo.return && (
                <div className="data-item">
                  <h4>📥 Receiving Data</h4>
                  <p>{dataInfo.return}</p>
                </div>
              )}

              {dataInfo.example && (
                <div className="data-item">
                  <h4>💡 FileMaker Example</h4>
                  <pre className="code-example">{dataInfo.example}</pre>
                </div>
              )}
            </section>
          )}

          {/* FileMaker Integration Notes */}
          <section className="notes-section">
            <h3>📝 Integration Notes</h3>
            <ul>
              <li><strong>Hide Navigation:</strong> When <code>tool</code> parameter is present, navigation is hidden by default. Add <code>showToolbar=true</code> to show it.</li>
              <li><strong>Web Viewer Object:</strong> Name your FileMaker web viewer object "FMwvTool" for JavaScript communication.</li>
              <li><strong>URL Encoding:</strong> Use FileMaker's <code>GetAsURLEncoded()</code> function for parameter values.</li>
              <li><strong>JSON Data:</strong> Use <code>JSONSetElement()</code> to build JSON data structures.</li>
              <li><strong>Script Communication:</strong> Use <code>FileMaker.PerformScript()</code> from JavaScript to call FileMaker scripts.</li>
            </ul>
          </section>
        </div>
      </div>

      <style>{`
        .url-info-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }

        .url-info-modal {
          background: white;
          border-radius: 12px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .url-info-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 2px solid #e5e7eb;
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }

        .url-info-header h2 {
          margin: 0;
          font-size: 20px;
          color: #111827;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 32px;
          color: #6b7280;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .close-button:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .url-info-content {
          padding: 24px;
        }

        .url-info-content section {
          margin-bottom: 32px;
        }

        .url-info-content section:last-child {
          margin-bottom: 0;
        }

        .url-info-content h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }

        .url-info-content h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .url-display {
          display: flex;
          gap: 12px;
          align-items: center;
          background: #f9fafb;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .url-display code {
          flex: 1;
          font-size: 13px;
          color: #1f2937;
          word-break: break-all;
        }

        .copy-button {
          padding: 6px 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          white-space: nowrap;
          transition: background 0.2s;
        }

        .copy-button:hover {
          background: #2563eb;
        }

        .params-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .params-table th {
          background: #f9fafb;
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .params-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #e5e7eb;
          color: #1f2937;
        }

        .params-table code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 12px;
          color: #1f2937;
        }

        .data-item {
          margin-bottom: 20px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border-left: 4px solid #3b82f6;
        }

        .data-item:last-child {
          margin-bottom: 0;
        }

        .data-item p {
          margin: 0;
          color: #374151;
          font-size: 14px;
        }

        .code-example {
          background: #1f2937;
          color: #f9fafb;
          padding: 16px;
          border-radius: 6px;
          overflow-x: auto;
          font-size: 12px;
          line-height: 1.6;
          margin: 8px 0 0 0;
        }

        .notes-section ul {
          margin: 0;
          padding-left: 20px;
          color: #374151;
        }

        .notes-section li {
          margin-bottom: 12px;
          line-height: 1.6;
        }

        .notes-section li:last-child {
          margin-bottom: 0;
        }

        .notes-section code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 12px;
          color: #1f2937;
        }

        @media (prefers-color-scheme: dark) {
          .url-info-modal {
            background: #1f2937;
            color: #f9fafb;
          }

          .url-info-header {
            border-bottom-color: #374151;
            background: #1f2937;
          }

          .url-info-header h2 {
            color: #f9fafb;
          }

          .close-button {
            color: #9ca3af;
          }

          .close-button:hover {
            background: #374151;
            color: #f9fafb;
          }

          .url-info-content h3,
          .url-info-content h4 {
            color: #f9fafb;
          }

          .url-display {
            background: #111827;
            border-color: #374151;
          }

          .url-display code {
            color: #f9fafb;
          }

          .params-table th {
            background: #111827;
            color: #f9fafb;
            border-bottom-color: #374151;
          }

          .params-table td {
            border-bottom-color: #374151;
            color: #f9fafb;
          }

          .params-table code,
          .notes-section code {
            background: #111827;
            color: #f9fafb;
          }

          .data-item {
            background: #111827;
            border-left-color: #3b82f6;
          }

          .data-item p,
          .notes-section li {
            color: #d1d5db;
          }
        }
      `}</style>
    </div>
  );
};
